import { MapPin } from 'lucide-preact';
import { useState } from 'preact/hooks';
import { DateRangePicker } from './DateRangePicker.js';
import { formatDisplay } from './useDateTimePicker.js';
import type { AllowedWindow, OccupiedWindow } from './useDateTimePicker.js';

export interface TripContextStripProps {
  /** Display only — the car's own fixed pickup point, never the searched location. */
  locationName?: string;
  pickup: Date | null;
  return: Date | null;
  onChange: (range: { pickup: Date | null; return: Date | null }) => void;
  minDate?: Date;
  maxDate?: Date;
  allowedWindow?: AllowedWindow | null;
  /** 205 (#292): already-committed windows, greyed out in both the pickup and return pickers. */
  occupiedWindows?: OccupiedWindow[];
  disabled?: boolean;
  /** BookingFormPage needs the picker visible immediately; CarDetailPage starts collapsed. */
  startExpanded?: boolean;
}

function summaryText(pickup: Date | null, ret: Date | null): string {
  if (!pickup && !ret) return 'Add your trip dates';
  const pickupText = pickup ? formatDisplay(pickup) : 'Add pickup';
  const returnText = ret ? formatDisplay(ret) : 'Add return';
  return `${pickupText} – ${returnText}`;
}

// 126: one shared component so CarDetailPage and BookingFormPage don't each carry
// their own copy of "show the searched window, let the customer edit it in place."
export function TripContextStrip({
  locationName,
  pickup,
  return: returnValue,
  onChange,
  minDate,
  maxDate,
  allowedWindow,
  occupiedWindows,
  disabled,
  startExpanded = false,
}: TripContextStripProps) {
  const [expanded, setExpanded] = useState(startExpanded);

  return (
    <div className="flex flex-col gap-2">
      {locationName && (
        <p
          data-testid="trip-context-location"
          className="flex items-center gap-1.5 text-sm text-text"
        >
          <MapPin size={14} className="text-text-muted shrink-0" />
          {locationName}
        </p>
      )}
      {!expanded && (
        <button
          type="button"
          data-testid="trip-context-summary"
          onClick={() => setExpanded(true)}
          disabled={disabled}
          className="w-full text-left text-sm text-text bg-surface-mid rounded-lg px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {summaryText(pickup, returnValue)}
        </button>
      )}
      {expanded && (
        <DateRangePicker
          pickup={pickup}
          return={returnValue}
          onChange={onChange}
          minDate={minDate}
          maxDate={maxDate}
          allowedWindow={allowedWindow}
          occupiedWindows={occupiedWindows}
          disabled={disabled}
        />
      )}
    </div>
  );
}
